import {
  AGENTIC_LOGIC_SURFACE_LABEL,
  type AgenticLogicSurface,
} from "@intelligems/ig-types/dtos/agentic-logic/surface";
import { type AgenticLogicValidationScenarioInput } from "@intelligems/ig-types/functions/agentic-logic/gates/validation-scenario";
import { currencyMinorUnitDigits } from "@intelligems/ig-utils/minor-units";

/**
 * The parts of a run's request the canvas says back to the merchant: which
 * surface the cart was judged on, what delivery was selected at checkout,
 * and how many orders the shopper had placed. Everything else the request
 * carries (the cart lines) is already what the merchant picked.
 */
export interface RuleGraphScenarioContext {
  surface: AgenticLogicSurface;
  delivery?: {
    isLocalPickup: boolean;
    /** Whether the request names a selected option at all. */
    chosen: boolean;
    selectedTitle?: string;
    /** Integer minor units of `currency`. */
    selectedCost?: number;
    currency?: string;
    transitDays?: { min?: number; max?: number };
  };
  customer?: { ordersCount: number };
}

export const buildScenarioContext = (
  request: AgenticLogicValidationScenarioInput["request"]
): RuleGraphScenarioContext => {
  const context: RuleGraphScenarioContext = { surface: request.surface };
  const delivery = request.shipping.delivery;
  if (delivery) {
    const selected = delivery.selected;
    context.delivery = {
      isLocalPickup: delivery.isLocalPickup,
      chosen: selected !== undefined,
      ...(selected?.title !== undefined
        ? { selectedTitle: selected.title }
        : {}),
      ...(selected?.cost !== undefined ? { selectedCost: selected.cost } : {}),
      currency: selected?.currency ?? request.shipping.currency,
      ...(selected?.transitDays !== undefined
        ? { transitDays: selected.transitDays }
        : {}),
    };
  }
  const ordersCount = request.customer?.ordersCount;
  if (typeof ordersCount === "number") {
    context.customer = { ordersCount };
  }
  return context;
};

const formatCost = (cost: number, currency: string | undefined): string => {
  if (cost === 0) {
    return "Free";
  }
  const code = currency ?? "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
    }).format(cost / 10 ** currencyMinorUnitDigits(code));
  } catch {
    // An unknown currency code throws; the amount still means something.
    return `${cost / 10 ** currencyMinorUnitDigits(code)} ${code}`;
  }
};

const formatTransit = (
  transitDays: { min?: number; max?: number } | undefined
): string | null => {
  if (transitDays === undefined) {
    return null;
  }
  const { min, max } = transitDays;
  if (min !== undefined && max !== undefined) {
    return min === max ? `${min} days` : `${min}–${max} days`;
  }
  if (min !== undefined) {
    return `from ${min} days`;
  }
  if (max !== undefined) {
    return `up to ${max} days`;
  }
  return null;
};

/** The delivery half of the caption: "Local pickup", "Standard $4.90 · 5–8 days". */
export const formatScenarioDelivery = (
  delivery: NonNullable<RuleGraphScenarioContext["delivery"]>
): string => {
  if (delivery.isLocalPickup) {
    return delivery.selectedTitle ?? "Local pickup";
  }
  if (!delivery.chosen) {
    return "Delivery not chosen yet";
  }
  const parts = [delivery.selectedTitle ?? "Delivery"];
  if (delivery.selectedCost !== undefined) {
    parts.push(formatCost(delivery.selectedCost, delivery.currency));
  }
  const head = parts.join(" ");
  const transit = formatTransit(delivery.transitDays);
  return transit === null ? head : `${head} · ${transit}`;
};

/**
 * One caption line under "What the shopper sees": the surface, and the
 * delivery when the request names one — "Checkout · Local pickup",
 * "Checkout · Express $14.90 · 1–2 days", "Onsite".
 */
export const formatScenarioContext = (
  context: RuleGraphScenarioContext
): string => {
  const surface = AGENTIC_LOGIC_SURFACE_LABEL[context.surface];
  return context.delivery === undefined
    ? surface
    : `${surface} · ${formatScenarioDelivery(context.delivery)}`;
};
